import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Stack,
  Chip,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Upload as UploadIcon,
  Image as ImageIcon,
  VideoCameraBack as VideoIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { settingApi } from '../../api/api';
import { useNavigate } from 'react-router-dom';
export default function AddAdvertisement() {
  const [adType, setAdType] = useState('image');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [duration, setDuration] = useState(15);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return alert('Please enter a title.');
    if (!url.trim()) return alert('Please enter a target URL.');
    if (!file) return alert(`Please upload a ${adType} file.`);
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('type', adType);
    formData.append('title', title);
    formData.append('url', url);
    formData.append('duration', duration);
    formData.append('file', file);
    try {
      const response = await settingApi.storeAdvertisement(formData);
      if (response.data.success) {
        alert('Advertisement created successfully!');
        resetForm();
        navigate('/admin/Advertisement');
      } else {
        alert(response.data.message || 'Failed to create advertisement');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.response?.data?.message || 'An error occurred while creating the advertisement');
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    setAdType('image');
    setTitle('');
    setUrl('');
    setDuration(15);
    setFile(null);
    setPreviewUrl('');
  };
  return (
    <Box sx={{ maxWidth: 'md', mx: 'auto', p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
          New Advertisement
        </Typography>
      </Stack>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Ad Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'medium' }}>
              Advertisement Type
            </FormLabel>
            <RadioGroup
              row
              value={adType}
              onChange={(e) => setAdType(e.target.value)}
              sx={{ gap: 2 }}
            >
              <FormControlLabel
                value="image"
                control={<Radio color="primary" />}
                label={
                  <Chip
                    icon={<ImageIcon />}
                    label="Image"
                    color={adType === 'image' ? 'primary' : 'default'}
                    variant={adType === 'image' ? 'filled' : 'outlined'}
                    sx={{ px: 2 }}
                  />
                }
                sx={{ m: 0 }}
              />
              <FormControlLabel
                value="video"
                control={<Radio color="secondary" />}
                label={
                  <Chip
                    icon={<VideoIcon />}
                    label="Video"
                    color={adType === 'video' ? 'secondary' : 'default'}
                    variant={adType === 'video' ? 'filled' : 'outlined'}
                    sx={{ px: 2 }}
                  />
                }
                sx={{ m: 0 }}
              />
            </RadioGroup>
          </FormControl>
          <Divider sx={{ my: 3 }} />
          {/* Title and URL */}
          <Stack spacing={3} sx={{ mb: 4 }}>
            <TextField
              label="Advertisement Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Target URL"
              type="url"
              variant="outlined"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              placeholder="https://example.com"
            />
          </Stack>
          <Divider sx={{ my: 3 }} />
          {/* Media Upload */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {adType === 'image' ? 'Image' : 'Video'} Content
            </Typography>
            <Button
              component="label"
              variant="outlined"
              color="primary"
              startIcon={<UploadIcon />}
              sx={{ mb: 2 }}
            >
              Upload {adType === 'image' ? 'Image' : 'Video'}
              <input
                type="file"
                accept={adType === 'image' ? 'image/*' : 'video/*'}
                hidden
                onChange={handleFileChange}
                required
              />
            </Button>
            {file && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected file: {file.name}
              </Typography>
            )}
            {/* Preview Section */}
            {previewUrl && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                {adType === 'image' ? (
                  <Avatar
                    variant="rounded"
                    src={previewUrl}
                    sx={{
                      width: '100%',
                      height: 300,
                      maxWidth: 500,
                      borderRadius: 2,
                      boxShadow: 2,
                      mx: 'auto'
                    }}
                  />
                ) : (
                  <Box
                    component="video"
                    src={previewUrl}
                    controls
                    sx={{
                      width: '100%',
                      maxWidth: 500,
                      height: 300,
                      borderRadius: 2,
                      boxShadow: 2,
                      mx: 'auto',
                      bgcolor: 'black'
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 3 }} />
          {/* Duration */}
          <TextField
            label="Display Duration (seconds)"
            type="number"
            variant="outlined"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            inputProps={{ min: 1, max: 60 }}
            fullWidth
            required
            sx={{ mb: 4 }}
            InputLabelProps={{ shrink: true }}
          />
          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/Advertisement')}
              size="large"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              size="large"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Publishing...' : 'Publish Advertisement'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}